import { all, call, put, select } from 'redux-saga/effects';
import { organizationSelector } from 'selectors/Organization';
import { currentOrganizationUUIDSelector } from 'selectors/Dashboard';
import { Organization } from 'models/Organization';

import { getBenchmarkStandings } from './BenchmarkStandings';
import { getBiggestChanges } from './BiggestChanges';
import { getContributionVolume } from './ContributionVolume';
import { getOrganizationBreakdown } from './OrganizationBreakdown';
import { getProjectStats } from './ProjectStats';
import { getPullRequestContribution } from './PullRequestContribution';
import { getReviewLifecycleDuration } from './ReviewLifecycleDuration';
import { getMetricsHighlights } from './MetricsHighlights';
import { fetchOrganizationMembers } from '../organizationMembers';
import { fetchMissingMembers } from '../organizations';

import { actions as currentOrganizationUUIDActions } from 'reducers/Dashboard/CurrentOrganizationUUID';
import { actions as benchmarkStatndingsActions } from 'reducers/Dashboard/BenchmarkStandings';
import { actions as biggestChangesActions } from 'reducers/Dashboard/BiggestChanges';
import { actions as contributionVolumeActions } from 'reducers/Dashboard/ContributionVolume';
import { actions as authorHighlightsActions } from 'reducers/Dashboard/Highlights/AuthorHighlights';
import { actions as didYouKnowActions } from 'reducers/Dashboard/Highlights/DidYouKnow';
import { actions as projectHighlightsActions } from 'reducers/Dashboard/Highlights/ProjectHighlights';
import { actions as reviewerHighlightsActions } from 'reducers/Dashboard/Highlights/ReviewerHighlights';
import { actions as orgBreakdownActions } from 'reducers/Dashboard/OrganizationBreakdown';
import { actions as projectStatActions } from 'reducers/Dashboard/ProjectStats';
import { actions as pullRequestContributionActions } from 'reducers/Dashboard/PullRequestContribution';
import { actions as reviewLifecycleActions } from 'reducers/Dashboard/PullRequestContribution';

export function* fetchDashboardData() {
  try {
    const organization: Organization = yield select(organizationSelector);
    const currentOrganizationUUID: string = yield select(
      currentOrganizationUUIDSelector
    );

    if (!organization || organization.uuid === currentOrganizationUUID) {
      return;
    }

    yield call(resetDashboard);

    const { uuid } = organization;
    yield call(refreshDashboardData, uuid);
  } catch {
    console.error('An error occurred in the fetchDashboardData saga');
  }
}

export function* resetDashboard() {
  yield all([
    put(benchmarkStatndingsActions.reset()),
    put(biggestChangesActions.reset()),
    put(contributionVolumeActions.reset()),
    put(authorHighlightsActions.resetAuthorHighlights()),
    put(didYouKnowActions.resetDidYouKnow()),
    put(projectHighlightsActions.resetProjectHighlights()),
    put(reviewerHighlightsActions.resetReviewerHighlights()),
    put(orgBreakdownActions.reset()),
    put(projectStatActions.reset()),
    put(pullRequestContributionActions.reset()),
    put(reviewLifecycleActions.reset()),
  ]);
}

export function* refreshDashboardData(uuid: string) {
  // Sets the current organization UUID to determine whether or not we need to fetch new dashboard data
  yield put(currentOrganizationUUIDActions.setCurrentOrganizationUUID(uuid));
  /**
   * This data is needed when displaying membership onboarding widget and
   * for the highlights widgets. Fetching this first so that there is no race condition
   * where the data isn't yet populated before trying to access in down stream sagas
   */
  yield all([
    fetchOrganizationMembers({
      organizationUuid: uuid,
    }),
    fetchMissingMembers({
      organizationUuid: uuid,
    }),
  ]);

  yield all([
    getBenchmarkStandings({
      organizationUUID: uuid,
    }),
    getBiggestChanges({
      organizationUUID: uuid,
    }),
    getContributionVolume({
      organizationUUID: uuid,
    }),
    getOrganizationBreakdown({
      organizationUUID: uuid,
    }),
    getProjectStats({
      organizationUUID: uuid,
    }),
    getPullRequestContribution({
      organizationUUID: uuid,
    }),
    getReviewLifecycleDuration({
      organizationUUID: uuid,
    }),
    getMetricsHighlights({
      organizationUUID: uuid,
    }),
  ]);
}
